import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { useFetchLoans } from '../../../hooks/loan/useFetchLoans';
import { Loan, LoanStatus } from '../../../interfaces';
import Page from '../../../layout/components/Page';
import LoanTypeFilter from '../../Common/LoanTypeFilter';
import Modal from '../../Common/Modal';
import LoadingIndicator from '../../Common/LoadingIndicator';
import BorrowerLoanRow from './BorrowerLoanRow';
import Paginate from '../../Common/Paginate';
import { NoContent } from '../../Common/NoContent';

const BorrowerLoans = () => {
  const { error, showForm, loanTypes, page, menu, loading, openLoans, underwritingLoans, activeLoans, closedLoans, pagination, pageQuery, updateLoanTypeValues, handleFilterLoan, toggleForm, makeActive, fetchLoans } = useFetchLoans();

  const renderLoans = (loans: Loan[]) => {
    return error ?
    (<div className="card">
      <div className="d-flex justify-content-center align-item-center card-body">
        <div className="text-center pt-20 pb-20">
          <p className="text-muted ms-20p me-20p pt-3 pb-3">An error occured, please try again.</p>
          <button onClick={() => fetchLoans()} className="btn btn-warning btn-sm">
            <span className="fw-bolder">Refresh</span>
          </button>
        </div>
      </div>
    </div>) : (
      <>
        <div className='table-responsive'>
          <table className='table table-bordered align-middle gs-0 gy-4'>
            <thead>
              <tr className='fw-bolder text-muted bg-light'>
                <th className='ps-4 min-w-325px rounded-start'>Loan Title</th>
                <th className='min-w-100px'>Amount</th>
                <th className='min-w-100px'>Repayment</th>
                {menu !== LoanStatus.OPEN && <th className='min-w-70px'>Interest</th>}
                <th className='text-end rounded-end'></th>
              </tr>
            </thead>
            <tbody>
              {loans.map((loan) => (
                <BorrowerLoanRow key={loan.id} loan={loan} menu={menu} />
              ))}
            </tbody>
          </table>
        </div>

        {pagination && <Paginate pagination={pagination} pageLink={`/loans/${menu}`} pageQuery={pageQuery} />}
      </>
    );
  };

  const RenderChildren = (
    <>
      <button className='btn btn-sm btn-flex btn-light-primary btn-active-primary fw-bolder me-2' onClick={toggleForm}>
        <i className='bi bi-filter'></i>
        Filter Loans
      </button>

      <Link to='/loans/apply' className='btn btn-sm btn-primary' data-bs-toggle='modal' data-bs-target='#kt_modal_create_app' id='kt_toolbar_primary_button'>
        Apply For Loan
      </Link>
    </>
  );
  return (
    <>
      <Page title='Manage Loans' icon='bi-briefcase' toolbarChildren={RenderChildren}>
        <div className='card'>
          <div className='card-body'>
            <div className='d-flex overflow-auto h-55px mb-5'>
              <ul className='nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-6 fw-bolder flex-nowrap'>
                <li className='nav-item'>
                  <Link className={clsx('nav-link text-active-primary me-6 text-capitalize', makeActive(LoanStatus.OPEN))} to={`/loans/${LoanStatus.OPEN}${pageQuery()}`}>
                    {LoanStatus.OPEN}
                  </Link>
                </li>
                <li className='nav-item'>
                  <Link className={clsx('nav-link text-active-primary me-6 text-capitalize', makeActive(LoanStatus.UNDERWRITING))} to={`/loans/${LoanStatus.UNDERWRITING}${pageQuery()}`}>
                    {LoanStatus.UNDERWRITING}
                  </Link>
                </li>
                <li className='nav-item'>
                  <Link className={clsx('nav-link text-active-primary me-6 text-capitalize', makeActive(LoanStatus.ONGOING))} to={`/loans/${LoanStatus.ONGOING}${pageQuery()}`}>
                    {LoanStatus.ONGOING}
                  </Link>
                </li>
                <li className='nav-item'>
                  <Link className={clsx('nav-link text-active-primary me-6 text-capitalize', makeActive(LoanStatus.CLOSED))} to={`/loans/${LoanStatus.CLOSED}${pageQuery()}`}>
                    {LoanStatus.CLOSED}
                  </Link>
                </li>
              </ul>
            </div>

            {loading && ((menu === LoanStatus.OPEN && !openLoans) || (menu === LoanStatus.UNDERWRITING && !underwritingLoans) || (menu === LoanStatus.ONGOING && !activeLoans) || (menu === LoanStatus.CLOSED && !closedLoans)) && <LoadingIndicator message='' />}

            {menu === LoanStatus.OPEN && page === 1 && openLoans && !openLoans.length && (
              <div className='d-flex justify-content-center align-item-center'>
                <div className='text-center pt-20 pb-20'>
                  <i className='bi bi-folder' style={{ fontSize: '3.5em' }}></i>
                  <p className='text-muted ms-20p me-20p pt-3 pb-3'>It's so quiet in here! There are several investors waiting for you. Click on the button below to apply for your first loan.</p>
                  <Link type='submit' className='btn btn-primary' to='/loans/apply'>
                    Apply For Loan
                  </Link>
                </div>
              </div>
            )}

            {page === 1 && ((menu === LoanStatus.UNDERWRITING && underwritingLoans && !underwritingLoans.length) || (menu === LoanStatus.ONGOING && activeLoans && !activeLoans.length) || (menu === LoanStatus.CLOSED && closedLoans && !closedLoans.length)) && (
              <NoContent message={`You currently have no ${menu} loans.`} />
            )}

            {menu === LoanStatus.OPEN && openLoans && openLoans.length ? renderLoans(openLoans) : null}

            {menu === LoanStatus.UNDERWRITING && underwritingLoans && underwritingLoans.length ? renderLoans(underwritingLoans) : null}

            {menu === LoanStatus.ONGOING && activeLoans && activeLoans.length ? renderLoans(activeLoans) : null}

            {menu === LoanStatus.CLOSED && closedLoans && closedLoans.length ? renderLoans(closedLoans) : null}
          </div>
        </div>
      </Page>

      <Modal show={showForm} toggle={toggleForm} title='Filter Loan'>
        <label className='fs-8 text-muted'>Loan Types</label>
        <LoanTypeFilter defaultValues={loanTypes} updateSelectedValue={updateLoanTypeValues} />

        <button className='btn btn-sm btn-light-primary fw-bolder text-center mt-10 align-center' data-kt-menu-dismiss='true' onClick={handleFilterLoan}>
          Apply
        </button>
      </Modal>
    </>
  );
};

export default BorrowerLoans;
